import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { adminFetch } from '../../utils/adminApi';
import SeoFieldsForm from './SeoFieldsForm';
import ImageUrlField from './ImageUrlField';
import './AdminSeoPanels.css';

const emptyPost = { title: '', excerpt: '', content: '', featuredImage: '', featuredImageAlt: '', categories: '', tags: '', publishDate: '', author: '', status: 'draft' };

const BlogAdminPanel = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(emptyPost);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const res = await adminFetch('/api/blog/admin/all');
    if (res.ok) setPosts(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setLoading(true);
    const payload = {
      ...form,
      categories: typeof form.categories === 'string' ? form.categories.split(',').map((s) => s.trim()).filter(Boolean) : form.categories,
      tags: typeof form.tags === 'string' ? form.tags.split(',').map((s) => s.trim()).filter(Boolean) : form.tags,
    };
    const url = editingId ? `/api/blog/${editingId}` : '/api/blog';
    const res = await adminFetch(url, { method: editingId ? 'PUT' : 'POST', body: JSON.stringify(payload) });
    setLoading(false);
    if (res.ok) {
      Swal.fire('Saved', 'Blog post saved.', 'success');
      setForm(emptyPost);
      setEditingId(null);
      load();
    } else Swal.fire('Error', 'Could not save.', 'error');
  };

  return (
    <div className="admin-seo-panel animate-in">
      <h2 className="admin-seo-panel-title">{editingId ? 'Edit Blog Post' : 'Add Blog Post'}</h2>
      <p className="admin-seo-panel-desc">Create blog posts with SEO slug, meta tags, categories and publish date.</p>

      <section className="admin-seo-section">
        <h3>Post Details</h3>
        <div className="form-row one-col">
          <label>Title
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Post title" />
          </label>
        </div>
        <div className="form-row">
          <label>Author
            <input value={form.author || ''} onChange={(e) => setForm({ ...form, author: e.target.value })} />
          </label>
          <label>Publish Date
            <input type="date" value={form.publishDate ? form.publishDate.slice(0, 10) : ''} onChange={(e) => setForm({ ...form, publishDate: e.target.value })} />
          </label>
        </div>
        <div className="form-row one-col">
          <label>Excerpt
            <textarea rows={2} value={form.excerpt || ''} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Short summary for listing pages" />
          </label>
        </div>
        <div className="form-row one-col">
          <label>Content
            <textarea rows={10} value={form.content || ''} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Full post content (HTML supported)" />
          </label>
        </div>
        <div className="form-row one-col">
          <ImageUrlField
            label="Featured Image"
            value={form.featuredImage || ''}
            onChange={(url) => setForm({ ...form, featuredImage: url })}
            folder="blog"
            placeholder="https://... or upload"
          />
        </div>
        <div className="form-row one-col">
          <label>Featured Image Alt
            <input value={form.featuredImageAlt || ''} onChange={(e) => setForm({ ...form, featuredImageAlt: e.target.value })} />
          </label>
        </div>
        <div className="form-row three-col">
          <label>Categories (comma-separated)
            <input value={typeof form.categories === 'string' ? form.categories : (form.categories || []).join(', ')} onChange={(e) => setForm({ ...form, categories: e.target.value })} />
          </label>
          <label>Tags (comma-separated)
            <input value={typeof form.tags === 'string' ? form.tags : (form.tags || []).join(', ')} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </label>
          <label>Status
            <select value={form.status || 'draft'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
        </div>
      </section>

      <SeoFieldsForm title="Blog Post SEO" slugPrefix="/blog/" values={form} onChange={(seo) => setForm({ ...form, ...seo })} />

      <div className="admin-seo-actions">
        <button type="button" className="action-submit-btn" onClick={save} disabled={loading}>{editingId ? 'Update Post' : 'Publish Post'}</button>
        {editingId && <button type="button" className="action-submit-btn secondary" onClick={() => { setForm(emptyPost); setEditingId(null); }}>Cancel</button>}
      </div>

      {posts.length > 0 && (
        <section className="admin-seo-section" style={{ marginTop: 24 }}>
          <h3>Existing Posts</h3>
          {posts.map((p) => (
            <div key={p._id} className="list-row">
              <span>{p.title} <em>({p.status})</em> — /blog/{p.slug}</span>
              <button type="button" className="action-submit-btn secondary" onClick={() => { setForm(p); setEditingId(p._id); }}>Edit</button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
};

export default BlogAdminPanel;
